/**
 * API base-URL resolution, isolated so it is testable without importing the
 * config modules (which validate Firebase env at import time).
 *
 * Why this is not a one-liner: webpack's `EnvironmentPlugin` substitutes a
 * value for EVERY declared key, so `process.env.API_BASE_URL` is never
 * `undefined` in a web bundle — it is the declared default. A `??` fallback
 * therefore never fires, and a default of `'http://localhost:8080'` gets
 * constant-folded into the production bundle, pointing the console at the
 * user's own machine. That shipped (adveserial.md A1). The plugin default is
 * now `''` and emptiness is what means "unset".
 */

/** The subset of `window.location` this resolver needs. */
export interface OriginLike {
  origin: string;
  hostname: string;
}

const DEV_API = 'http://localhost:8080';
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/**
 * Resolve the API base URL.
 *
 * - an explicit non-empty `API_BASE_URL` always wins;
 * - otherwise, in a browser, the API serves this build, so same-origin is
 *   correct (and keeps the Firebase ID token on one origin);
 * - except under the webpack dev server, which serves the build itself on a
 *   different port and proxies `/v1` — there the API is elsewhere.
 *
 * `dev` is what separates the last two, and a LOCAL HOSTNAME IS NOT THAT TEST.
 * It was, and it was wrong in one case that matters: the end-to-end run serves
 * a production bundle from the Go service on 127.0.0.1, where same-origin is
 * both correct and required — the app was calling `localhost:8080` from a page
 * on `127.0.0.1:8080`, which the browser treats as cross-origin and blocks.
 * The hostname check still guards the case it was written for, but only while
 * this is a development build.
 */
export function resolveApiBaseUrl(
  envValue: string | undefined,
  loc?: OriginLike,
  dev = process.env.NODE_ENV !== 'production',
): string {
  const explicit = envValue?.trim();
  if (explicit) {
    return explicit;
  }
  if (loc && !(dev && LOCAL_HOSTS.includes(loc.hostname))) {
    return loc.origin;
  }
  return DEV_API;
}
